// Slot default content is not in the page's element tree, so an edit to it has
// nowhere to go until someone works out which component owns the content, which
// slot field holds it, and what the field's new value should be. This file plans
// that write. It composes the read/locate/transform helpers in SlotDefaultUtils
// with path utilities, and it stays free of Flux — planning is data in, data out,
// so callers can ask where an edit would land without performing it.
import {last} from 'lodash';
import {weakMemo} from '@packages/utilities/memo';
import {namesEqual} from '@packages/systems/wfdl-Qualified';
import {
  Component,
  Expressions,
  Plugins,
  Types,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  EElement,
  EList,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  getGuidNullable,
  type NativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {
  applyListItemAction,
  type ExpressionListItemAction,
} from '@packages/utilities/platform-listUtils';
import {
  addElementAtAnchorById,
  getComponentInstRootElement,
  isUserComponentInst,
  reassignElementIdsForOverride,
  removeElementById,
} from '@packages/utilities/core-elements';
// Direct-file import to skip barrels that would re-enter platform mid-load.
// eslint-disable-next-line webflow/module-top-level-imports
import {isPathToSymbolRoot} from '@packages/utilities/native-id-paths/NativeIdPathUtils';
// Direct-file import for the same reason: the element-clone-utils barrel also
// exports cloneElementWithoutComponentPropertyBindings, which imports platform,
// and that barrel shims back into this package.
// eslint-disable-next-line webflow/module-top-level-imports
import {refreshElementIds} from '@packages/systems/element-clone-utils/refreshElementIds';
import {
  findSlotDefaultElementAcrossTrees,
  getEffectiveSlotDefaultExpression,
  getLiveElementAtNativeIdPath,
  getSlotDefaultExpression,
  resolveEffectiveSlotDefault,
  resolveSlotDefaultPathContext,
  setSlotDefaultExpression,
} from './SlotDefaultUtils';
import {getComponentSlots, getSlotOverrideFieldId} from './SlotElementUtils';

export type SlotDefaultEdit =
  | {kind: 'data'; editKey: ExpressionPath; newExpression: Expression}
  | {
      kind: 'dataBatch';
      changes: Array<{editKey: ExpressionPath; newExpression: Expression}>;
    }
  | {kind: 'reset'; key: ExpressionPath}
  | {kind: 'listItem'; key: ExpressionPath; action: ExpressionListItemAction};

export type SlotDefaultMutation =
  | {kind: 'edit'; edit: SlotDefaultEdit}
  | {kind: 'promote'}
  | {kind: 'delete'}
  // The copy's ids are counted up from `seedId`. The caller supplies it the way
  // CONTEXT_MENU_DUPLICATE carries one, which keeps guid generation out of this
  // file and lets tests pin the ids they expect.
  | {kind: 'duplicate'; seedId: string};

/**
 * One link in a chain of nested slot defaults: the instance that owns the
 * content, the slot field holding it, and the element being written.
 */
export type SlotDefaultLevel = {
  ownerInstancePath: NativeId[];
  ownerInstanceId: string;
  ownerComponentType: ExpressionElement['type'];
  slotFieldId: string;
  elementId: string;
  ignoreOwnerOverride?: true;
  // The path ran on into this instance's render, so `elementId` is the deepest
  // thing it could name rather than the thing it named. Fine to place beside or
  // to delete whole, never the thing to apply an edit to.
  stopsInsideRender?: true;
};

const setElementDataAtPath = (
  element: ExpressionElement,
  key: ExpressionPath,
  value: Expression
): ExpressionElement => {
  const nextHoleyData = Expressions.sparseSetIn(
    Expressions.toHoleyExpression(Expressions.getElementData(element)),
    key,
    value
  );
  const nextData = Expressions.fromHoleyExpression(nextHoleyData);
  if (!nextData) return element;
  return Expressions.setElementData(element, nextData);
};

const deleteElementDataAtPath = (
  element: ExpressionElement,
  key: ExpressionPath
): ExpressionElement =>
  Expressions.setElementData(
    element,
    Expressions.deleteInRecord(Expressions.getElementData(element), key)
  );

const buildSlotDefaultEditUpdater = (
  edit: SlotDefaultEdit
): ((element: ExpressionElement) => ExpressionElement) => {
  if (edit.kind === 'reset') {
    return (element) => deleteElementDataAtPath(element, edit.key);
  }

  // List actions are relative to the list already at `key`, so an element whose
  // data never held that field has nothing to add to, move within, or remove
  // from. Bails the same way the live render does in componentMapReducer.
  if (edit.kind === 'listItem') {
    return (element) => {
      const currentList = Expressions.getIn(
        Expressions.getElementData(element),
        edit.key
      );
      if (!currentList) return element;
      const nextList = applyListItemAction(currentList, edit.action);
      if (!nextList) return element;
      return setElementDataAtPath(element, edit.key, nextList);
    };
  }

  if (edit.kind === 'dataBatch') {
    return (element) => {
      const nextHoleyData = edit.changes.reduce(
        (holey, {editKey, newExpression}) =>
          Expressions.sparseSetIn(holey, editKey, newExpression),
        Expressions.toHoleyExpression(Expressions.getElementData(element))
      );
      const nextData = Expressions.fromHoleyExpression(nextHoleyData);
      if (!nextData) return element;
      return Expressions.setElementData(element, nextData);
    };
  }

  return (element) =>
    setElementDataAtPath(element, edit.editKey, edit.newExpression);
};

/**
 * `expression` with a copy of `elementId` inserted right after it. The copy's
 * ids are minted from `seedId`, because a definition write lands in defaultData
 * verbatim and nothing downstream re-mints them. The mirror of
 * `removeElementById`.
 */
const duplicateElementById = (
  expression: Expression,
  elementId: string,
  seedId: string
): Expression => {
  const element = Expressions.getElementById(expression, elementId);
  if (!element) return expression;
  const {refreshedElement} = refreshElementIds(element, seedId);
  return addElementAtAnchorById(
    expression,
    refreshedElement,
    elementId,
    'after'
  );
};

/**
 * The slot default list with `mutation` applied to `elementId`. Promotion writes
 * this expression straight onto the instance as an override; definition edits
 * write the expression into the component's defaultData.
 */
export const applySlotDefaultMutationToExpression = (
  expression: Expression,
  elementId: string,
  mutation: SlotDefaultMutation
): Expression => {
  switch (mutation.kind) {
    case 'edit':
      return Expressions.updateElement(
        elementId,
        buildSlotDefaultEditUpdater(mutation.edit),
        expression
      );
    case 'promote':
      return expression;
    case 'delete':
      return removeElementById(expression, elementId);
    case 'duplicate':
      return duplicateElementById(expression, elementId, mutation.seedId);
  }
};

export const applySlotDefaultMutation = (
  defaultElements: ReadonlyArray<ExpressionElement>,
  elementId: string,
  mutation: SlotDefaultMutation
): Expression =>
  applySlotDefaultMutationToExpression(
    EList(defaultElements.map((element) => EElement(element))),
    elementId,
    mutation
  );

type AuthoredPathMatch = {
  deepestElement: ExpressionElement;
  deepestElementPathIndex: number;
  missingElementId: string | null;
};

const findDeepestAuthoredElement = (
  nativeIdPath: NativeId[],
  currentPathIndex: number,
  targetPathLength: number,
  authoredExpression: Expression
): AuthoredPathMatch | null => {
  let deepestElement: ExpressionElement | null = null;
  let deepestElementPathIndex = -1;
  let missingElementId: string | null = null;

  for (
    let pathIndex = currentPathIndex;
    pathIndex < targetPathLength;
    pathIndex++
  ) {
    const elementId = getGuidNullable(nativeIdPath[pathIndex]);
    if (!elementId) return null;
    const element = Expressions.getElementById(authoredExpression, elementId);
    if (!element) {
      missingElementId = elementId;
      break;
    }
    deepestElement = element;
    deepestElementPathIndex = pathIndex;
  }

  if (!deepestElement) return null;
  return {deepestElement, deepestElementPathIndex, missingElementId};
};

export const setComponentSlotDefaultExpression = ({
  componentMap,
  componentType,
  fieldId,
  expression,
}: {
  componentMap: Component.ComponentMap;
  componentType: ExpressionElement['type'];
  fieldId: string;
  expression: Expression;
}): Component.ComponentMap | null => {
  const component = componentMap.get(componentType);
  if (!component) return null;
  const field = Types.getRecordFields(Component.getDataType(component))?.[
    fieldId
  ];
  if (!field) return null;

  // Unmigrated components have nowhere to put this: slot defaults are never
  // written back to system.defaultValue, which is what the property migration
  // trips over. Decline so the caller aborts instead of dropping the write.
  const defaultData = Component.getDefaultData(component);
  if (!defaultData) return null;

  return componentMap.set(
    componentType,
    Component.update(component, {
      defaultData: setSlotDefaultExpression(defaultData, fieldId, expression),
    })
  );
};

/**
 * Inserts `element` at the start or end of a SlotContent field's default list
 * and writes the result to the component's `defaultData`. Declines the same way
 * `setComponentSlotDefaultExpression` does when the component has no
 * `defaultData` or the field doesn't exist.
 */
export const insertSlotDefaultElement = ({
  componentMap,
  componentType,
  fieldId,
  element,
  position,
}: {
  componentMap: Component.ComponentMap;
  componentType: ExpressionElement['type'];
  fieldId: string;
  element: ExpressionElement;
  position: 'start' | 'end';
}): Component.ComponentMap | null => {
  const component = componentMap.get(componentType);
  if (!component) return null;
  // Without this, a non-slot field's default would be overwritten with a list.
  const field = Types.getRecordFields(Component.getDataType(component))?.[
    fieldId
  ];
  if (!field || !Types.isSlotContentType(field)) return null;
  const existingExpression = getSlotDefaultExpression(component, fieldId);
  if (!existingExpression) return null;

  const existingItems = Expressions.getList(existingExpression) ?? [];
  return setComponentSlotDefaultExpression({
    componentMap,
    componentType,
    fieldId,
    expression: EList(
      position === 'start'
        ? [EElement(element), ...existingItems]
        : [...existingItems, EElement(element)]
    ),
  });
};

/**
 * Removes the top-level default `elementId` from a SlotContent field and writes
 * the result to the component's `defaultData`. Declines the same way
 * `insertSlotDefaultElement` does, and when `elementId` is not a top-level
 * entry of the field's defaults.
 */
export const removeSlotDefaultElement = ({
  componentMap,
  componentType,
  fieldId,
  elementId,
}: {
  componentMap: Component.ComponentMap;
  componentType: ExpressionElement['type'];
  fieldId: string;
  elementId: string;
}): Component.ComponentMap | null => {
  const component = componentMap.get(componentType);
  if (!component) return null;
  const field = Types.getRecordFields(Component.getDataType(component))?.[
    fieldId
  ];
  if (!field || !Types.isSlotContentType(field)) return null;
  const existingExpression = getSlotDefaultExpression(component, fieldId);
  if (!existingExpression) return null;

  // removeElementById reaches nested elements too, so the top-level check
  // keeps a nested id from deleting content the caller never listed.
  const isTopLevelDefault = (
    Expressions.getList(existingExpression) ?? []
  ).some((item) => Expressions.getElement(item)?.id === elementId);
  if (!isTopLevelDefault) return null;

  return setComponentSlotDefaultExpression({
    componentMap,
    componentType,
    fieldId,
    expression: applySlotDefaultMutationToExpression(
      existingExpression,
      elementId,
      {kind: 'delete'}
    ),
  });
};

/**
 * Walks a native id path down through nested slot defaults, outermost first.
 * Each level names the instance whose slot field holds the next stretch of the
 * path. Null when the path is not slot default content, or when a segment can't
 * be accounted for at all.
 *
 * A path that carries on into an instance's own render stops at that instance.
 * Nothing inside a render is authorable from out here, so the instance is the
 * deepest thing the path names and callers place beside it.
 */
const resolveSlotDefaultWriteChainUncached = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): SlotDefaultLevel[] | null => {
  const outer = resolveSlotDefaultPathContext(
    nativeIdPath,
    componentMap,
    plugins
  );
  if (!outer) return null;

  const targetPathLength = isPathToSymbolRoot(nativeIdPath, componentMap)
    ? nativeIdPath.length - 1
    : nativeIdPath.length;

  const levels: SlotDefaultLevel[] = [];
  let ownerInstancePath = outer.ownerInstancePath;
  let ownerComponentType = outer.ownerComponentType;
  let slotFieldId = outer.slotFieldId;
  let ownerElement = getLiveElementAtNativeIdPath(
    ownerInstancePath,
    componentMap
  );
  let ignoreOwnerOverride = Boolean(outer.ignoreOwnerOverride);
  if (ignoreOwnerOverride) {
    ownerElement = null;
  }
  let currentPathIndex = ownerInstancePath.length;

  for (;;) {
    const ownerInstanceId = getGuidNullable(last(ownerInstancePath));
    if (!ownerInstanceId) return null;

    const authoredExpression = getEffectiveSlotDefaultExpression(
      plugins,
      componentMap,
      ownerComponentType,
      slotFieldId,
      ownerElement
    );
    if (!authoredExpression) return null;
    const match = findDeepestAuthoredElement(
      nativeIdPath,
      currentPathIndex,
      targetPathLength,
      authoredExpression
    );
    if (!match) return null;

    const level: SlotDefaultLevel = {
      ownerInstancePath,
      ownerInstanceId,
      ownerComponentType,
      slotFieldId,
      elementId: match.deepestElement.id,
    };
    if (ignoreOwnerOverride) {
      level.ignoreOwnerOverride = true;
    }
    levels.push(level);

    if (!match.missingElementId) return levels;
    if (!isUserComponentInst(componentMap, match.deepestElement)) return null;

    const deeperHit = findSlotDefaultElementAcrossTrees(
      plugins,
      componentMap,
      match.deepestElement.type,
      match.missingElementId,
      match.deepestElement
    );
    if (!deeperHit) {
      const instanceRoot = getComponentInstRootElement(componentMap)(
        match.deepestElement
      );
      const isInsideRender = Boolean(
        instanceRoot &&
        Expressions.getElementById(
          EElement(instanceRoot),
          match.missingElementId
        )
      );
      if (!isInsideRender) return null;
      level.stopsInsideRender = true;
      return levels;
    }

    ownerInstancePath = nativeIdPath.slice(
      0,
      match.deepestElementPathIndex + 1
    );
    ownerComponentType = match.deepestElement.type;
    slotFieldId = deeperHit.slotFieldId;
    ownerElement = match.deepestElement;
    ignoreOwnerOverride = deeperHit.ignoredInstanceOverride;
    if (ignoreOwnerOverride) {
      ownerElement = null;
    }
    currentPathIndex = match.deepestElementPathIndex + 1;
  }
};

const memoizedResolveSlotDefaultWriteChain = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo((componentMap: Component.ComponentMap) =>
      weakMemo((nativeIdPath: NativeId[]) =>
        resolveSlotDefaultWriteChainUncached(
          nativeIdPath,
          componentMap,
          plugins
        )
      )
    )
);

export const resolveSlotDefaultWriteChain = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): SlotDefaultLevel[] | null =>
  memoizedResolveSlotDefaultWriteChain(plugins)(componentMap)(nativeIdPath);

/**
 * The value for the outermost level's slot field, with the mutation applied at
 * the innermost level and each intermediate level rebuilt around it. Promoting
 * nested default content has to write the whole nesting in one override, since
 * only the outermost instance is a real element on the page.
 */
/**
 * Where a normalized chain's outermost expression has to be written.
 *
 * `componentDefault` is the definition you are editing — the content is authored
 * there, so the write goes straight to it. `instanceOverride` is copy-on-write:
 * the content came from a shared default and now belongs to one instance.
 */
export type SlotDefaultWriteLocation =
  | {
      kind: 'componentDefault';
      componentType: ExpressionElement['type'];
      fieldId: string;
    }
  | {
      kind: 'instanceOverride';
      componentType: ExpressionElement['type'];
      instanceId: string;
      fieldId: string;
    };

export type NormalizedSlotDefaultChain = {
  componentMap: Component.ComponentMap;
  location: SlotDefaultWriteLocation;
  expression: Expression;
  idMap: Record<string, string>;
};

// A forked copy has to avoid every id the project already holds, including the
// default it was copied from — that one keeps its own.
const allElementIds = weakMemo(
  (componentMap: Component.ComponentMap): ReadonlySet<string> => {
    const elementIds = new Set<string>();
    componentMap.forEach((component) => {
      for (const elementId of Object.keys(
        Expressions.allElements(Component.getRender(component))
      )) {
        elementIds.add(elementId);
      }
      const defaults = Component.getDefaultData(component);
      if (!defaults) return;
      for (const elementId of Object.keys(Expressions.allElements(defaults))) {
        elementIds.add(elementId);
      }
    });
    return elementIds;
  }
);

const setSlotOverrideOnElement = (
  element: ExpressionElement,
  slotFieldId: string,
  value: Expression
): ExpressionElement => {
  const data = Expressions.fromHoleyExpression(
    Expressions.sparseSetIn(
      Expressions.toHoleyExpression(Expressions.getElementData(element)),
      [{in: 'Record', at: slotFieldId}],
      value
    )
  );
  return data ? Expressions.setElementData(element, data) : element;
};

/**
 * Makes every level of a slot-default chain directly editable, and writes the
 * result into the ComponentMap.
 *
 * Each level is one of two things. Already authored where it sits — an explicit
 * override, or a default belonging to the component being edited — in which case
 * it is edited in place and keeps its ids. Or projected from a shared default, in
 * which case it is forked into an override, and the fork is a copy so it gets ids
 * of its own.
 *
 * Nothing is removed here. Callers get a map where both ends of their move are
 * real, and do the move afterwards.
 */
export const normalizeSlotDefaultChain = (
  plugins: Plugins.PluginsType,
  componentMap: Component.ComponentMap,
  chainLevels: SlotDefaultLevel[],
  editingComponentType: ExpressionElement['type'] | null,
  resolveRenderOwner: (
    componentMap: Component.ComponentMap,
    ownerInstancePath: NativeId[]
  ) => ExpressionElement['type'] | null
): NormalizedSlotDefaultChain | null => {
  const editingLevelIndex =
    editingComponentType === null
      ? -1
      : chainLevels.findIndex((level) =>
          namesEqual(level.ownerComponentType, editingComponentType)
        );
  // Earlier defaults locate the edited definition but are outside this write.
  const levels =
    editingLevelIndex > 0 ? chainLevels.slice(editingLevelIndex) : chainLevels;

  const first = levels[0];
  if (!first) return null;

  // Read the project's ids straight off the memo and keep the ids this fork
  // mints beside it, rather than copying every id in the project to hold two.
  const projectElementIds = allElementIds(componentMap);
  const mintedElementIds = new Set<string>();
  const idMap: Record<string, string> = {};
  const expressions: Expression[] = [];

  const isAuthoredHere = (
    level: SlotDefaultLevel,
    origin: 'override' | 'default'
  ): boolean =>
    origin === 'override' ||
    Boolean(
      editingComponentType &&
      namesEqual(level.ownerComponentType, editingComponentType)
    );

  // Two different things stop a level being forked, and only one of them changes
  // where the outermost write goes. An existing override is edited in place on the
  // instance; a default belonging to the component being edited is edited in the
  // definition itself.
  const writesDefinition = Boolean(
    editingComponentType &&
    namesEqual(first.ownerComponentType, editingComponentType)
  );
  let ownerElement = getLiveElementAtNativeIdPath(
    first.ownerInstancePath,
    componentMap
  );
  if (first.ignoreOwnerOverride) ownerElement = null;

  for (let index = 0; index < levels.length; index++) {
    const level = levels[index];
    if (!level) return null;

    if (index > 0) {
      const previous = expressions[index - 1];
      if (!previous) return null;
      // The level above may have been forked, so the owner answers to its new id.
      ownerElement = Expressions.getElementById(
        previous,
        idMap[level.ownerInstanceId] ?? level.ownerInstanceId
      );
      if (level.ignoreOwnerOverride) ownerElement = null;
    }

    const source = resolveEffectiveSlotDefault(
      plugins,
      componentMap,
      level.ownerComponentType,
      level.slotFieldId,
      ownerElement
    );
    if (!source) return null;

    const authoredHere = isAuthoredHere(level, source.origin);

    let expression = source.expression;
    if (!authoredHere) {
      const forked = reassignElementIdsForOverride({
        namespace: level.ownerInstanceId,
        overrideId: level.slotFieldId,
        expression,
        isElementIdTaken: (elementId) =>
          projectElementIds.has(elementId) || mintedElementIds.has(elementId),
      });
      expression = forked.expression;
      for (const [authoredId, nextId] of Object.entries(forked.idMap)) {
        idMap[authoredId] = nextId;
        mintedElementIds.add(nextId);
      }
    }
    expressions.push(expression);
  }

  let nested = expressions[expressions.length - 1]!;
  for (let index = levels.length - 2; index >= 0; index--) {
    const childLevel = levels[index + 1];
    const parentExpression = expressions[index];
    if (!childLevel || !parentExpression) return null;
    nested = Expressions.updateElement(
      idMap[childLevel.ownerInstanceId] ?? childLevel.ownerInstanceId,
      (element) =>
        setSlotOverrideOnElement(element, childLevel.slotFieldId, nested),
      parentExpression
    );
  }

  if (writesDefinition) {
    const nextMap = setComponentSlotDefaultExpression({
      componentMap,
      componentType: first.ownerComponentType,
      fieldId: first.slotFieldId,
      expression: nested,
    });
    if (!nextMap) return null;
    return {
      componentMap: nextMap,
      location: {
        kind: 'componentDefault',
        componentType: first.ownerComponentType,
        fieldId: first.slotFieldId,
      },
      expression: nested,
      idMap,
    };
  }

  const renderOwnerType = resolveRenderOwner(
    componentMap,
    first.ownerInstancePath
  );
  if (!renderOwnerType) return null;
  const renderOwner = componentMap.get(renderOwnerType);
  if (!renderOwner) return null;

  const nextRender = Expressions.updateElement(
    first.ownerInstanceId,
    (element) => setSlotOverrideOnElement(element, first.slotFieldId, nested),
    Component.getRender(renderOwner)
  );
  return {
    componentMap: componentMap.set(
      renderOwnerType,
      Component.update(renderOwner, {render: nextRender})
    ),
    location: {
      kind: 'instanceOverride',
      componentType: renderOwnerType,
      instanceId: first.ownerInstanceId,
      fieldId: first.slotFieldId,
    },
    expression: nested,
    idMap,
  };
};

/**
 * The component's render with one Slot's children replaced by `contents`.
 *
 * A Slot's children come from its field, so nothing downstream of the field can
 * be found by walking a render. Grafting puts that content where a tree walk
 * expects it, which is what the placement checks need before they can judge a
 * drop into a Slot default. Only ever done to a throwaway map.
 */
export const graftSlotContentIntoRender = (
  componentMap: Component.ComponentMap,
  componentType: ExpressionElement['type'],
  slotFieldId: string,
  contents: Expression
): Component.ComponentMap => {
  const component = componentMap.get(componentType);
  if (!component) return componentMap;

  const slot = getComponentSlots(component).find(
    (candidate) => getSlotOverrideFieldId(candidate) === slotFieldId
  );
  if (!slot) return componentMap;

  const nextRender = Expressions.updateElement(
    slot.id,
    (element) =>
      Expressions.setElementData(
        element,
        Expressions.setIn(
          Expressions.getElementData(element),
          [{in: 'Record', at: 'children'}],
          contents
        )
      ),
    Component.getRender(component)
  );

  return componentMap.set(
    componentType,
    Component.update(component, {render: nextRender})
  );
};

/**
 * Writes an expression back to the location a normalized chain named.
 */
export const applySlotDefaultWrite = (
  componentMap: Component.ComponentMap,
  location: SlotDefaultWriteLocation,
  expression: Expression
): Component.ComponentMap | null => {
  if (location.kind === 'componentDefault') {
    return setComponentSlotDefaultExpression({
      componentMap,
      componentType: location.componentType,
      fieldId: location.fieldId,
      expression,
    });
  }

  const component = componentMap.get(location.componentType);
  if (!component) return null;
  return componentMap.set(
    location.componentType,
    Component.update(component, {
      render: Expressions.updateElement(
        location.instanceId,
        (element) =>
          setSlotOverrideOnElement(element, location.fieldId, expression),
        Component.getRender(component)
      ),
    })
  );
};

const buildNestedPromotedExpressionUncached = (
  plugins: Plugins.PluginsType,
  componentMap: Component.ComponentMap,
  levels: SlotDefaultLevel[],
  mutation?: SlotDefaultMutation
): Expression | null => {
  const first = levels[0];
  const innermost = last(levels);
  if (!first || !innermost) return null;
  // Promotion only re-homes the chain, so it survives a chain that stopped short
  // of what the path named. A delete lands on that innermost instance, which is
  // the authored default the path ran through, so removing the whole thing is
  // what the user asked for and it survives too. An edit would land on the
  // instance rather than the render element the path named, so it still bails.
  if (
    innermost.stopsInsideRender &&
    mutation &&
    mutation.kind !== 'promote' &&
    mutation.kind !== 'delete'
  ) {
    return null;
  }

  const expressions: Expression[] = [];
  let ownerElement = getLiveElementAtNativeIdPath(
    first.ownerInstancePath,
    componentMap
  );
  if (first.ignoreOwnerOverride) {
    ownerElement = null;
  }
  let expression = getEffectiveSlotDefaultExpression(
    plugins,
    componentMap,
    first.ownerComponentType,
    first.slotFieldId,
    ownerElement
  );
  if (!expression) return null;
  expressions.push(expression);

  for (let index = 1; index < levels.length; index++) {
    const level = levels[index];
    if (!level) return null;
    ownerElement = Expressions.getElementById(
      expression,
      level.ownerInstanceId
    );
    if (level.ignoreOwnerOverride) {
      ownerElement = null;
    }
    expression = getEffectiveSlotDefaultExpression(
      plugins,
      componentMap,
      level.ownerComponentType,
      level.slotFieldId,
      ownerElement
    );
    if (!expression) return null;
    expressions.push(expression);
  }

  let nestedExpression = mutation
    ? applySlotDefaultMutationToExpression(
        expressions[expressions.length - 1]!,
        innermost.elementId,
        mutation
      )
    : expressions[expressions.length - 1]!;

  for (let index = levels.length - 2; index >= 0; index--) {
    const childLevel = levels[index + 1];
    const levelParentExpression = expressions[index];
    if (!childLevel || !levelParentExpression) return null;
    nestedExpression = Expressions.updateElement(
      childLevel.ownerInstanceId,
      (element) =>
        setElementDataAtPath(
          element,
          [{in: 'Record', at: childLevel.slotFieldId}],
          nestedExpression
        ),
      levelParentExpression
    );
  }

  return nestedExpression;
};

// Promotion re-reads the whole nested default chain per pointer move, and the
// mutation-free shape is the one it asks for.
const memoizedBuildNestedPromotedExpression = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo((componentMap: Component.ComponentMap) =>
      weakMemo((levels: SlotDefaultLevel[]) =>
        buildNestedPromotedExpressionUncached(plugins, componentMap, levels)
      )
    )
);

export const buildNestedPromotedExpression = (
  plugins: Plugins.PluginsType,
  componentMap: Component.ComponentMap,
  levels: SlotDefaultLevel[],
  mutation?: SlotDefaultMutation
): Expression | null => {
  if (!mutation) {
    return memoizedBuildNestedPromotedExpression(plugins)(componentMap)(levels);
  }

  return buildNestedPromotedExpressionUncached(
    plugins,
    componentMap,
    levels,
    mutation
  );
};
